import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Video } from 'lucide-react';
import { VideoSectionContent, PageSectionProps } from '@/types/cms';
import { cn } from '@/lib/utils';

interface VideoSectionProps extends PageSectionProps {
  content: VideoSectionContent;
}

const VideoSection: React.FC<VideoSectionProps> = ({ 
  section, 
  content, 
  className,
  isPreview = false
}) => {
  const {
    video_url,
    video_thumbnail,
    video_description,
    video_type = 'youtube',
    autoplay = false,
    controls = true,
    layout = 'center'
  } = content as VideoSectionContent;

  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [showVideo, setShowVideo] = useState(autoplay); // Only used for direct video files
  const [thumbnailQuality, setThumbnailQuality] = useState<'maxresdefault' | 'hqdefault' | 'mqdefault' | 'default'>('hqdefault');

  // Helper function to get responsive class prefix
  const getResponsivePrefix = () => isPreview ? '@' : '';

  // Extract video ID based on type
  const getVideoId = (url: string, type: string) => {
    if (!url) return null;
    
    switch (type) {
      case 'youtube':
        const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
        return youtubeMatch ? youtubeMatch[1] : null;
      
      case 'vimeo':
        const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
        return vimeoMatch ? vimeoMatch[1] : null;
      
      default:
        return url; // Direct video URL
    }
  };

  const videoId = video_url ? getVideoId(video_url, video_type) : null;

  // Reset thumbnail quality when video changes
  useEffect(() => {
    setThumbnailQuality('hqdefault');
  }, [videoId, video_type]);

  // Generate YouTube thumbnail URL with quality parameter
  const getYouTubeThumbnail = (id: string, quality: string = 'maxresdefault') => {
    return `https://img.youtube.com/vi/${id}/${quality}.jpg`;
  };

  // Simple error handler - just log for now
  const handleThumbnailError = () => {
    console.log('Thumbnail error occurred for:', thumbnailUrl);
    console.log('Video type:', video_type, 'Video ID:', videoId);
    // For now, just log - hqdefault should work for all videos
  };

  // Determine which thumbnail to use
  const getThumbnailUrl = () => {
    // Priority: 1) Custom thumbnail, 2) Auto YouTube thumbnail, 3) None (generic)
    if (video_thumbnail) {
      console.log('Using custom thumbnail:', video_thumbnail);
      return video_thumbnail;
    }
    
    if (video_type === 'youtube' && videoId) {
      // Use current quality state for YouTube thumbnails
      const url = getYouTubeThumbnail(videoId, thumbnailQuality);
      console.log('Generated YouTube thumbnail URL:', url);
      return url;
    }
    
    console.log('No thumbnail available, returning null');
    return null;
  };

  const thumbnailUrl = getThumbnailUrl();

  // Generate embed URL
  const getEmbedUrl = () => {
    if (!videoId) return null;
    
    switch (video_type) {
      case 'youtube':
        return `https://www.youtube.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}&controls=${controls ? 1 : 0}&rel=0`;
      
      case 'vimeo':
        return `https://player.vimeo.com/video/${videoId}?autoplay=${autoplay ? 1 : 0}&controls=${controls ? 1 : 0}`;
      
      default:
        return videoId; // Direct video URL
    }
  };

  const embedUrl = getEmbedUrl();

  // Layout classes
  const containerClasses = cn(
    "py-16 px-4",
    {
      "text-left": layout === 'left',
      "text-right": layout === 'right',
      "text-center": layout === 'center',
    },
    className
  );

  const videoContainerClasses = cn(
    "relative",
    {
      "max-w-4xl mx-auto": layout === 'center',
      "max-w-4xl": layout === 'left' || layout === 'right',
      "w-full": layout === 'full_width',
    }
  );

  // Handle play button click (only for direct video files)
  const handlePlayClick = () => {
    setShowVideo(true);
    setIsPlaying(true);
  };

  return (
    <section className={cn("py-16 px-4", className)}>
      <div className="max-w-6xl mx-auto">
        {/* Section Header - outside the card like GridSection */}
        {section.title && (
          <div className="text-center mb-12">
            <h2 className={`text-3xl ${getResponsivePrefix()}md:text-4xl font-bold mb-4 text-gray-900 leading-tight`}>
              {section.title}
            </h2>
            {video_description && (
              <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto">
                {video_description}
              </p>
            )}
          </div>
        )}

        {/* Video Container - No card wrapper */}
        {video_url ? (
          <div className={videoContainerClasses}>
            {video_type === 'youtube' || video_type === 'vimeo' ? (
              // YouTube/Vimeo: Show iframe directly with native thumbnail
              <div className="aspect-video rounded-lg overflow-hidden shadow-md">
                <iframe
                  src={embedUrl || ''}
                  title={section.title || 'Video player'}
                  className="w-full h-full"
                  style={{ border: 'none' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              // Direct video files: Use custom thumbnail + video element
              !showVideo ? (
                // Enhanced Video Thumbnail with Play Button
                <div className="relative group cursor-pointer" onClick={handlePlayClick}>
                  <div className="aspect-video rounded-lg overflow-hidden bg-gray-900 shadow-md">
                    {thumbnailUrl ? (
                      <img 
                        src={thumbnailUrl}
                        alt={section.title || 'Video thumbnail'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onLoad={() => console.log('Image loaded successfully:', thumbnailUrl)}
                        onError={(e) => {
                          console.log('Image error event fired!', e);
                          handleThumbnailError();
                        }}
                      />
                    ) : (
                      // Enhanced default thumbnail (for non-YouTube or when thumbnail fails)
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                        <div className="text-center text-white">
                          <div className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                            <Play className="h-10 w-10 text-white opacity-90" />
                          </div>
                          <p className="text-lg font-medium">Play Video</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Enhanced Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white/95 hover:bg-white rounded-full p-4 group-hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl">
                      <Play className="h-8 w-8 text-gray-900 ml-1" />
                    </div>
                  </div>
                </div>
              ) : (
                // Direct video player
                <div className="aspect-video rounded-lg overflow-hidden shadow-md">
                  <video 
                    src={embedUrl || ''}
                    controls={controls}
                    autoPlay={autoplay}
                    className="w-full h-full"
                    poster={thumbnailUrl || undefined}
                  />
                </div>
              )
            )}
          </div>
        ) : (
          // Enhanced empty state with icon and better typography
          <div className="text-center py-16 space-y-4">
            <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
              <Video className="w-8 h-8 text-primary" />
            </div>
            <div className="space-y-2">
              <p className="text-lg font-medium text-gray-900">No video configured yet</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                Add a video URL through the admin panel to display engaging video content in this section.
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default VideoSection;